import { TextField } from "@mui/material";
import { MediaLibraryImageSelector } from "components/MediaLibraryImageSelector";
import { Field } from "react-final-form";
import { useIntl } from "react-intl";
import { SettingOptionItem } from "services/KnowfullyAdminClient";
import SettingsColorSelector from "./SettingsColorSelector";
import { SettingTypeId } from "types/common-types";
import FieldRichText from "components/FieldRichText";
import SettingsSwitch from "./SettingsSwitch";
import SettingJsonField, { validateJsonObject } from "./SettingJsonField";
import SettingsOptionsSelect from "./SettingsOptionsSelect";

type SettingValueFieldProps = {
  settingTypeId?: string;
  settingOptions?: SettingOptionItem[];
};

export default function SettingValueField({
  settingTypeId,
  settingOptions,
}: SettingValueFieldProps) {
  const { formatMessage: t } = useIntl();

  const validate = settingTypeId === SettingTypeId.Json
    ? (value: string) => validateJsonObject(value, t)
    : undefined;

  return (
    <Field name="value" parse={(value) => value ?? ""} validate={validate}>
      {({ input, meta }) => {
        if (settingOptions && settingOptions.length > 0) {
          return (
            <SettingsOptionsSelect
              settingOptions={settingOptions}
              error={meta.submitFailed ? meta.error : undefined}
              {...input}
            />
          );
        }

        if (settingTypeId === SettingTypeId.MediaLibraryAsset) {
          return <MediaLibraryImageSelector {...input} />;
        }

        if (settingTypeId === SettingTypeId.Color) {
          return (
            <SettingsColorSelector
              error={meta.error && meta.submitFailed}
              helperText={meta.error && meta.submitFailed && meta.error}
              {...input}
            />
          );
        }

        if (settingTypeId === SettingTypeId.Bool) {
          return (
            <SettingsSwitch
              {...input}
              initialChecked={"true" === input.value.toLowerCase()}
            />
          );
        }

        if (settingTypeId === SettingTypeId.Html) {
          return (
            <FieldRichText
              input={input}
              meta={meta}
              init={{
                height: 500,
              }}
            />
          );
        }

        if (settingTypeId === SettingTypeId.Json) {
          return (
            <SettingJsonField
              error={meta.error && meta.submitFailed}
              helperText={meta.error && meta.submitFailed && meta.error}
              {...input}
            />
          );
        }

        return (
          <TextField
            variant="standard"
            fullWidth
            label={t({ id: "valueLabel" })}
            error={meta.error && meta.submitFailed}
            helperText={meta.error && meta.submitFailed && meta.error}
            {...input}
          />
        );
      }}
    </Field>
  );
}
